import { z } from "zod";
import { isStatementRetrievalFinancialAccountType } from "@common/helpers/financialAccount";
import { FinancialAccountType } from "../types/financialAccount";
import { TaskType } from "../types/task";
import {
    CLOSE_TEMPLATE_REVIEW_FIELDS,
    CloseTemplateReviewField,
} from "../types/closeTemplateReviewField";
import {
    PersistableView,
    PersistableViewSchema,
    ViewType,
} from "../types/views/views";

export const closeTemplateReviewFieldSchema = z.enum(
    CLOSE_TEMPLATE_REVIEW_FIELDS,
);

const persistableTemplateViewSchema = PersistableViewSchema;

const monthsBeforeCloseSchema = z.number().int().min(0).max(36).nullable();

const financialAccountIdsSchema = z
    .array(z.number().int().positive().max(2147483647))
    .max(100);

function hasNonEmptyArray(value: Array<string | number> | undefined): boolean {
    return value != null && value.length > 0;
}

function refineExclusiveAccountTargets(
    data: {
        financialAccountType?: FinancialAccountType;
        financialAccountIds?: number[];
    },
    ctx: z.RefinementCtx,
): void {
    if (
        hasNonEmptyArray(data.financialAccountIds) &&
        data.financialAccountType != null
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
                "financialAccountIds and financialAccountType are mutually exclusive",
            path: ["financialAccountIds"],
        });
    }
}

function refineDocumentRetrievalDates(
    data: {
        startDateOverride?: string;
        endDateOverride?: string;
    },
    ctx: z.RefinementCtx,
): void {
    if (
        data.startDateOverride != null &&
        data.endDateOverride != null &&
        data.startDateOverride > data.endDateOverride
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "startDateOverride must be on or before endDateOverride",
            path: ["endDateOverride"],
        });
    }
}

function refineDocumentRetrievalViewConfig(
    data: {
        financialAccountType?: FinancialAccountType;
        financialAccountIds?: number[];
        startDateOverride?: string;
        endDateOverride?: string;
    },
    ctx: z.RefinementCtx,
): void {
    if (
        data.financialAccountType != null &&
        !isStatementRetrievalFinancialAccountType(data.financialAccountType)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
                "financialAccountType must be checking, savings, credit_card, or payment_processor",
            path: ["financialAccountType"],
        });
    }
    refineExclusiveAccountTargets(data, ctx);
    refineDocumentRetrievalDates(data, ctx);
}

function refineReconciliationTargets(
    data: z.infer<typeof persistableTemplateViewSchema>,
    ctx: z.RefinementCtx,
): void {
    if (
        hasNonEmptyArray(data.filters.accountIds) &&
        hasNonEmptyArray(data.filters.financialAccountTypes)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
                "filters.accountIds and filters.financialAccountTypes are mutually exclusive",
            path: ["filters", "accountIds"],
        });
    }
}

export const questionCloseTemplateViewConfigSchema = z.null();

export const transactionsCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.TRANSACTION),
        stickyViewType: z.enum(["sticky", "dynamic"]).optional(),
    });

export const transactionReviewCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.TRANSACTION),
        reviewFields: z.array(closeTemplateReviewFieldSchema).min(1).max(10),
    });

export const accrualReviewCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema
        .extend({
            type: z.literal(ViewType.TRANSACTION),
        })
        .superRefine((data, ctx) => {
            const buckets = data.filters.accrualSuggestionBuckets;
            if (!hasNonEmptyArray(buckets)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        "accrual_review viewConfig requires filters.accrualSuggestionBuckets",
                    path: ["filters", "accrualSuggestionBuckets"],
                });
            }
        });

export const profitLossCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.PROFIT_LOSS),
        numberOfMonthsToShowBeforeCurrentClosePeriods:
            monthsBeforeCloseSchema.optional(),
    });

export const balanceSheetCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.BALANCE_SHEET),
        numberOfMonthsToShowBeforeCurrentClosePeriods:
            monthsBeforeCloseSchema.optional(),
    });

export const documentRetrievalCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema
        .extend({
            type: z.literal(ViewType.FINANCIAL_DOCUMENT),
            financialAccountType: z.nativeEnum(FinancialAccountType).optional(),
            financialAccountIds: financialAccountIdsSchema.optional(),
            startDateOverride: z.string().date().optional(),
            endDateOverride: z.string().date().optional(),
        })
        .superRefine(refineDocumentRetrievalViewConfig);

export const reconciliationCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema
        .extend({
            type: z.literal(ViewType.RECONCILIATION),
            filters: persistableTemplateViewSchema.shape.filters.extend({
                financialAccountTypes: z
                    .array(z.nativeEnum(FinancialAccountType))
                    .max(20)
                    .optional(),
            }),
        })
        .superRefine(refineReconciliationTargets);

export const counterpartyDirectoryCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.COUNTERPARTY_DIRECTORY),
    });

export const billsCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.BILL),
    });

export const invoicesCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.INVOICE),
    });

export const financialDocumentsCloseTemplateViewConfigSchema =
    persistableTemplateViewSchema.extend({
        type: z.literal(ViewType.FINANCIAL_DOCUMENT),
    });

export type CloseTemplateTaskViewConfig =
    | null
    | (PersistableView & {
          stickyViewType?: "sticky" | "dynamic";
          reviewFields?: CloseTemplateReviewField[];
          numberOfMonthsToShowBeforeCurrentClosePeriods?: number | null;
          financialAccountType?: FinancialAccountType;
          financialAccountIds?: number[];
          startDateOverride?: string;
          endDateOverride?: string;
      });

const NO_VIEW_CONFIG = questionCloseTemplateViewConfigSchema;

const VIEW_CONFIG_SCHEMA_BY_TASK_TYPE: Record<
    TaskType,
    z.ZodType<CloseTemplateTaskViewConfig>
> = {
    [TaskType.QUESTION]: NO_VIEW_CONFIG,
    [TaskType.GET_DOCUMENTS]: NO_VIEW_CONFIG,
    [TaskType.TRANSACTIONS]: transactionsCloseTemplateViewConfigSchema,
    [TaskType.TRANSACTION_REVIEW]:
        transactionReviewCloseTemplateViewConfigSchema,
    [TaskType.ACCRUAL_REVIEW]: accrualReviewCloseTemplateViewConfigSchema,
    [TaskType.PROFIT_LOSS]: profitLossCloseTemplateViewConfigSchema,
    [TaskType.BALANCE_SHEET]: balanceSheetCloseTemplateViewConfigSchema,
    [TaskType.DOCUMENT_RETRIEVAL]:
        documentRetrievalCloseTemplateViewConfigSchema,
    [TaskType.RECONCILIATION]: reconciliationCloseTemplateViewConfigSchema,
    [TaskType.FORM_1099]: counterpartyDirectoryCloseTemplateViewConfigSchema,
    [TaskType.COUNTERPARTIES]:
        counterpartyDirectoryCloseTemplateViewConfigSchema,
    [TaskType.BILLS]: billsCloseTemplateViewConfigSchema,
    [TaskType.INVOICES]: invoicesCloseTemplateViewConfigSchema,
    [TaskType.FINANCIAL_DOCUMENTS]:
        financialDocumentsCloseTemplateViewConfigSchema,
    [TaskType.FILE]: NO_VIEW_CONFIG,
    [TaskType.TRANSACTION]: NO_VIEW_CONFIG,
    [TaskType.INVITE_ACCOUNTANT]: NO_VIEW_CONFIG,
    [TaskType.RECEIPT_PHONE_NUMBER]: NO_VIEW_CONFIG,
    [TaskType.CONNECTION_BROKEN]: NO_VIEW_CONFIG,
    [TaskType.PLAID_NEW_ACCOUNTS_AVAILABLE]: NO_VIEW_CONFIG,
    [TaskType.PLAID_STATEMENTS_REFRESH]: NO_VIEW_CONFIG,
    [TaskType.MAP_CHART_OF_ACCOUNTS]: NO_VIEW_CONFIG,
    [TaskType.CONNECT_ACCOUNT]: NO_VIEW_CONFIG,
    [TaskType.MAP_MIGRATED_DATA]: NO_VIEW_CONFIG,
    [TaskType.PROFIT_LOSS_ACTIVATION]: NO_VIEW_CONFIG,
    [TaskType.FILE_MIGRATION_UPLOAD]: NO_VIEW_CONFIG,
    [TaskType.XERO_FILE_UPLOAD]: NO_VIEW_CONFIG,
    [TaskType.QBDT_FILE_UPLOAD]: NO_VIEW_CONFIG,
    [TaskType.SCHEDULE_RULE_SUGGESTED_REVIEW]: NO_VIEW_CONFIG,
    [TaskType.SCHEDULE_SUSPICIOUS_TRANSACTION_REVIEW]: NO_VIEW_CONFIG,
    [TaskType.REVENUE_RECOGNITION_REVIEW]: NO_VIEW_CONFIG,
    [TaskType.SCHEDULE_SOURCE_CHANGE_REVIEW]: NO_VIEW_CONFIG,
    [TaskType.MILESTONE_REVENUE_RECOGNITION_REVIEW]: NO_VIEW_CONFIG,
    [TaskType.INTEGRATION_COA_MAPPING]: NO_VIEW_CONFIG,
};

export function viewConfigSchemaForTaskType(
    type: TaskType | null,
): z.ZodType<CloseTemplateTaskViewConfig> {
    if (type === null) {
        return NO_VIEW_CONFIG;
    }
    return VIEW_CONFIG_SCHEMA_BY_TASK_TYPE[type];
}
